import React, { useEffect, useState } from 'react';
import { Chip, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import Store from '../utilities/store';
import { StateManager } from '../utilities/stateManager';
import firebase from '../utilities/firebase';

// Presentation for each derived shipping-OUT status (see Store.getShippingStatus).
// Chip `color` values are fills here, so the .main palette variants are fine.
export const SHIPPING_STATUS_META = {
  not_shipped: { label: 'Not shipped', color: 'default', variant: 'outlined', icon: null,                       printBg: null,      tip: 'Not on a delivery load yet' },
  on_load:     { label: 'On load',     color: 'info',    variant: 'filled',   icon: LocalShippingOutlinedIcon,  printBg: '#0288d1', tip: 'Assigned to a load — not yet departed' },
  en_route:    { label: 'En route',    color: 'warning', variant: 'filled',   icon: LocalShippingIcon,          printBg: '#ed6c02', tip: 'Load is in progress' },
  delivered:   { label: 'Delivered',   color: 'success', variant: 'filled',   icon: CheckCircleIcon,            printBg: '#2e7d32', tip: 'Load completed — car delivered' },
};

const metaFor = (status) => SHIPPING_STATUS_META[status] || SHIPPING_STATUS_META.not_shipped;

// Resolves a car's derived shipping-out status + the drop-off load it sits on.
// Returns { status, loadId } (status null while loading). Shares Store's
// session-cached loads index, so many chips trigger one read.
export function useShippingInfo(stock, deal = null) {
  const [info, setInfo] = useState({ status: null, loadId: null });
  useEffect(() => {
    let active = true;
    if (!stock) { setInfo({ status: 'not_shipped', loadId: null }); return () => {}; }
    Store.getShippingInfo(stock, deal)
      .then(({ status, load }) => { if (active) setInfo({ status, loadId: load ? load.id : null }); })
      .catch(() => { if (active) setInfo({ status: deal && deal.shipping_complete === true ? 'delivered' : 'not_shipped', loadId: null }); });
    return () => { active = false; };
  }, [stock, deal && deal.shipping_complete]);
  return info;
}

// Thin wrapper for callers that only need the status string.
export function useShippingStatus(stock, deal = null) {
  return useShippingInfo(stock, deal).status;
}

// Drop-in chip. Pass an explicit `status`, or `stock` (+ optional `deal`) to
// have it derive on its own. When the car is on a load, clicking opens that
// load in the global load modal (pass clickable={false} to disable).
//
// `allowUnship` opts into an un-ship action: only when the chip shows
// "Delivered" AND the car is NOT on a load (i.e. the delivered state comes from
// the legacy `shipping_complete` flag — the "marked shipped by hand" case),
// clicking opens a one-item menu that clears `shipping_complete`, putting the
// car back on the shipping map. Cars on a real load keep the open-load click;
// those are un-shipped by removing them from the load. `onUnshipped` is called
// after a successful write so the caller can refresh its local deal copy.
export default function ShippingStatusChip({ status, stock, deal = null, size = 'small', showTooltip = true, clickable = true, allowUnship = false, onUnshipped, sx, ...rest }) {
  const { status: resolvedStatus, loadId } = useShippingInfo(stock, deal);
  const [anchorEl, setAnchorEl] = useState(null);
  const [busy, setBusy] = useState(false);
  const effective = status || resolvedStatus;
  if (!effective) return null; // still resolving

  const meta = metaFor(effective);
  const Icon = meta.icon;

  const canOpenLoad = clickable && !!loadId && typeof StateManager.openLoadModal === 'function';
  // Un-ship only applies to the legacy-boolean "delivered" case (no load).
  const canUnship = allowUnship && effective === 'delivered' && !loadId && !!stock;
  const canClick = canOpenLoad || canUnship;

  const handleClick = canUnship
    ? (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }
    : canOpenLoad
      ? (e) => { e.stopPropagation(); StateManager.openLoadModal(loadId); }
      : undefined;

  const handleUnship = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await firebase.firestore().doc('deals/' + stock).set(
        { shipping_complete: false, shipping_status: '' },
        { merge: true }
      );
      StateManager.setAlertAndOpen('Car put back on the shipping map', 'success');
      if (typeof onUnshipped === 'function') onUnshipped();
    } catch (err) {
      console.error('Error putting car back on shipping map:', err);
      StateManager.setAlertAndOpen('Error putting car back on the shipping map', 'error');
    } finally {
      setBusy(false);
      setAnchorEl(null);
    }
  };

  const chip = (
    <Chip
      size={size}
      label={meta.label}
      color={meta.color}
      variant={meta.variant}
      icon={Icon ? <Icon /> : undefined}
      onClick={handleClick}
      clickable={canClick}
      sx={{
        fontWeight: 500,
        ...(canClick ? { cursor: 'pointer' } : {}),
        ...(meta.color === 'default' ? { color: 'text.secondary' } : {}),
        printColorAdjust: 'exact',
        WebkitPrintColorAdjust: 'exact',
        '@media print': {
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
          colorAdjust: 'exact',
          ...(meta.printBg ? { backgroundColor: meta.printBg, color: '#fff' } : {}),
        },
        ...sx,
      }}
      {...rest}
    />
  );

  const tip = canUnship
    ? `${meta.tip} · click to put back on the shipping map`
    : canOpenLoad
      ? `${meta.tip} · click to open load #${loadId}`
      : meta.tip;

  return (
    <>
      {showTooltip ? <Tooltip title={tip}>{chip}</Tooltip> : chip}
      {canUnship && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => !busy && setAnchorEl(null)}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleUnship} disabled={busy}>
            <ListItemIcon>
              {busy ? <CircularProgress size={18} /> : <SettingsBackupRestoreIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary="Put back on shipping map" />
          </MenuItem>
        </Menu>
      )}
    </>
  );
}
